import React from "react";
import {
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

// Smart tick calculation function
function calculateSmartTicks(
  min: number,
  max: number,
  normMin: number,
  normMax: number,
  minTicks: number,
  maxTicks: number,
  preferIntegerTicks: boolean,
): number[] {
  const range = max - min;

  // For small ranges with integer preference (like visceral fat)
  if (preferIntegerTicks && range <= 10) {
    const ticks: number[] = [];
    for (let i = Math.floor(min); i <= Math.ceil(max); i++) {
      ticks.push(i);
    }

    // Add norm range boundaries if not already included
    if (!ticks.includes(normMin)) {
      ticks.push(normMin);
    }
    if (!ticks.includes(normMax)) {
      ticks.push(normMax);
    }

    return [...new Set(ticks)].sort((a, b) => a - b);
  }

  // Standard tick calculation
  let tickCount: number;
  if (range <= 10) {
    tickCount = Math.min(maxTicks, Math.max(minTicks, 5));
  } else if (range <= 50) {
    tickCount = Math.min(maxTicks, Math.max(minTicks, 6));
  } else {
    tickCount = Math.min(maxTicks, Math.max(minTicks, 8));
  }

  const ticks: number[] = [];
  const step = range / (tickCount - 1);

  for (let i = 0; i < tickCount; i++) {
    const tick = min + step * i;
    ticks.push(Math.round(tick * 100) / 100);
  }

  // Add norm range boundaries if not already included
  if (!ticks.includes(normMin)) {
    ticks.push(normMin);
  }
  if (!ticks.includes(normMax)) {
    ticks.push(normMax);
  }

  return [...new Set(ticks)].sort((a, b) => a - b);
}

export interface LineChartDataPoint {
  [key: string]: string | number | undefined;
}

export interface LineChartProps {
  data: LineChartDataPoint[];
  xKey: string;
  yKey: string;
  color?: string;
  height?: number;
  className?: string;
  yAxisDomain?: [number, number];
  yAxisTicks?: number[];
  yAxisTickFormatter?: (value: number) => string;
  gridLines?: {
    horizontal?: boolean;
    vertical?: boolean;
    horizontalColor?: string;
    verticalColor?: string;
    horizontalValues?: number[];
  };
  colorKey?: string; // Key for dynamic colors in data
  referenceLines?: {
    y?: Array<{
      value: number;
      stroke?: string;
      strokeDasharray?: string;
      strokeWidth?: number;
      label?: {
        value: string;
        position?: "insideTopRight" | "insideTopLeft" | "insideBottomRight" | "insideBottomLeft";
        fill?: string;
        fontSize?: number;
      };
    }>;
  };
  // Smart axis calculation
  smartAxis?: {
    enabled?: boolean;
    normRange?: {
      min: number;
      max: number;
    };
    padding?: number; // Percentage padding around data (default: 10%)
    minTicks?: number; // Minimum number of ticks
    maxTicks?: number; // Maximum number of ticks
    preferIntegerTicks?: boolean; // For small ranges, prefer integer ticks
  };
}

export function LineChart({
  data,
  xKey,
  yKey,
  color = "#ffffff", // white line for better contrast on gradient
  height = 256,
  className,
  yAxisDomain,
  yAxisTicks,
  yAxisTickFormatter,
  gridLines,
  colorKey,
  referenceLines,
  smartAxis,
}: LineChartProps) {
  // Smart axis calculation
  const calculatedAxis = React.useMemo(() => {
    if (!smartAxis?.enabled || !data.length) {
      return {
        domain: yAxisDomain,
        ticks: yAxisTicks,
        tickFormatter: yAxisTickFormatter,
      };
    }

    // Extract valid values from data
    const validValues = data
      .map((item) => item[yKey])
      .filter((value): value is number => {
        if (typeof value !== "number") return false;
        return value !== null && !isNaN(value);
      });

    if (validValues.length === 0) {
      return {
        domain: yAxisDomain,
        ticks: yAxisTicks,
        tickFormatter: yAxisTickFormatter,
      };
    }

    const dataMin = Math.min(...validValues);
    const dataMax = Math.max(...validValues);
    const normMin = smartAxis.normRange?.min ?? dataMin;
    const normMax = smartAxis.normRange?.max ?? dataMax;
    const padding = smartAxis.padding ?? 0.1; // 10% padding

    // Calculate smart domain
    const range = dataMax - dataMin;
    const paddingAmount = range * padding;

    let min = Math.max(0, dataMin - paddingAmount);
    let max = dataMax + paddingAmount;

    // Special handling for small ranges (like visceral fat)
    if (smartAxis.preferIntegerTicks && range <= 10) {
      min = Math.floor(min);
      max = Math.ceil(max);

      // Ensure we show at least the norm range
      if (smartAxis.normRange) {
        min = Math.min(min, Math.floor(smartAxis.normRange.min));
        max = Math.max(max, Math.ceil(smartAxis.normRange.max));
      }
    }

    // Calculate smart ticks
    const ticks = calculateSmartTicks(
      min,
      max,
      normMin,
      normMax,
      smartAxis.minTicks ?? 5,
      smartAxis.maxTicks ?? 8,
      smartAxis.preferIntegerTicks ?? false,
    );

    return {
      domain: [min, max] as [number, number],
      ticks,
      tickFormatter: yAxisTickFormatter,
    };
  }, [data, yKey, smartAxis, yAxisDomain, yAxisTicks, yAxisTickFormatter]);

  return (
    <div
      className={`w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-lg p-4 ${className}`}
      style={{ height: `${height}px` }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ReLineChart data={data} margin={{ top: 5, right: 50, left: 20, bottom: 5 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={gridLines?.horizontal ?? true}
            vertical={gridLines?.vertical ?? true}
            stroke={gridLines?.horizontalColor ?? "rgba(255, 255, 255, 0.1)"}
            {...(gridLines?.vertical === false ? { verticalPoints: [] } : {})}
          />
          <XAxis dataKey={xKey} stroke="#ffffff" fontSize={12} tickLine={false} axisLine={false} />
          <YAxis
            stroke="#ffffff"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            domain={calculatedAxis.domain}
            ticks={calculatedAxis.ticks}
            tickFormatter={calculatedAxis.tickFormatter}
            orientation="right"
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#111827",
              border: "1px solid #374151",
              color: "#F9FAFB",
              borderRadius: "8px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            }}
            labelStyle={{ color: "#D1D5DB" }}
          />

          {/* Reference Lines */}
          {referenceLines?.y?.map((refLine, index) => (
            <ReferenceLine
              key={index}
              y={refLine.value}
              stroke={refLine.stroke || "#10B981"}
              strokeDasharray={refLine.strokeDasharray || "3 3"}
              strokeWidth={refLine.strokeWidth || 1}
              {...(refLine.label ? { label: refLine.label } : {})}
            />
          ))}
          {colorKey ? (
            // Create segments for each level change
            (() => {
              interface LineSegment {
                data: LineChartDataPoint[];
                color: string | number | undefined;
              }
              const segments: LineSegment[] = [];
              let currentSegment: LineChartDataPoint[] = [];
              let currentColor = data[0]?.[colorKey] || color;

              data.forEach((item) => {
                const itemColor = item[colorKey] || color;

                if (itemColor !== currentColor && currentSegment.length > 0) {
                  // Color changed, create segment
                  segments.push({
                    data: [...currentSegment],
                    color: currentColor,
                  });
                  currentSegment = [item];
                  currentColor = itemColor;
                } else {
                  currentSegment.push(item);
                }
              });

              // Add last segment
              if (currentSegment.length > 0) {
                segments.push({
                  data: currentSegment,
                  color: currentColor,
                });
              }

              return segments.map((segment, index) => {
                const colorStr =
                  typeof segment.color === "string"
                    ? segment.color
                    : String(segment.color ?? color);
                return (
                  <Line
                    key={index}
                    type="monotone"
                    dataKey={yKey}
                    stroke={colorStr}
                    strokeWidth={2}
                    dot={{ fill: colorStr, strokeWidth: 2, r: 4 }}
                    activeDot={{
                      r: 6,
                      stroke: colorStr,
                      strokeWidth: 2,
                      fill: "#111827",
                    }}
                    data={segment.data}
                    connectNulls={false}
                  />
                );
              });
            })()
          ) : (
            // Single color
            <Line
              type="monotone"
              dataKey={yKey}
              stroke={color}
              strokeWidth={2}
              dot={{ fill: color, strokeWidth: 2, r: 4 }}
              activeDot={{
                r: 6,
                stroke: color,
                strokeWidth: 2,
                fill: "#111827",
              }}
            />
          )}
        </ReLineChart>
      </ResponsiveContainer>
    </div>
  );
}
